import {
  createFetch,
  type AfterFetchContext,
  type BeforeFetchContext,
  type CreateFetchOptions,
  type OnFetchErrorContext,
  type UseFetchReturn,
} from "@vueuse/core"
//@ts-ignore
import {arrayBufferToBase64, stringToArrayBuffer, arrayBufferToString} from "@/net/utils.js"
//@ts-ignore
import transportCtrl from "@/net/transport.js"
import {useAuthStore} from "./auth"
import {RoutesEnum} from "@/router/types"
import router from "@/router"
import {useErrorStore} from "./error"
import type {ApiError} from "./error/types"

export interface ExtendedFetchOptions extends RequestInit {
  $skipEncryption?: boolean
  $tempToken?: string
  $useToken?: boolean
  $sign?: boolean
  $browserId?: string
  $beforeSend?: (options: ExtendedFetchOptions, context: {url: string}) => void
  encrypted?: boolean
  requestKey?: CryptoKey
}

const abortControllers = new Map<string, AbortController>()

let useFetchFn: ((url: string, options?: RequestInit) => UseFetchReturn<any>) | null = null

// https://vueuse.org/core/useFetch/#creating-a-custom-instance
export const useFetch = <TRes = any>(url: string, options?: RequestInit) => {
  if (!useFetchFn) {
    useFetchFn = createFetch({
      baseUrl: import.meta.env.VITE_BACK_API_URL,
      options: {
        async beforeFetch({url, options}: BeforeFetchContext) {
          // Explicitly cast options to ExtendedFetchOptions
          const extendedOptions = options as ExtendedFetchOptions

          extendedOptions.headers = new Headers(extendedOptions.headers)
          extendedOptions.headers.set("lang", "uk-UA") // Replace with `global locale value` if available
          extendedOptions.headers.set(
            "content-type",
            options?.method === "DELETE" ? "text/plain" : "application/json"
          )

          if (!extendedOptions.$skipEncryption) {
            extendedOptions.encrypted = true
            try {
              // Auth with TempToken
              if (extendedOptions.$tempToken) {
                extendedOptions.headers.set("authorization", extendedOptions.$tempToken)
              }

              await transportCtrl.update()
              const requestTime = transportCtrl.getTime()
              const requestKey = await transportCtrl.getRequestKey(url.split("?")[0], requestTime)
              extendedOptions.requestKey = requestKey

              extendedOptions.headers.set("x-browser-id", transportCtrl.data.browserId)
              extendedOptions.headers.set("x-time", requestTime)

              if (!extendedOptions.headers.has("content-type")) {
                extendedOptions.headers.set("content-type", "application/octet-stream")
              }

              // Encrypt Token
              const token = transportCtrl.getToken()
              if (
                !extendedOptions.$tempToken &&
                extendedOptions.$useToken &&
                token &&
                token !== ""
              ) {
                extendedOptions.headers.set(
                  "x-token",
                  arrayBufferToBase64(
                    await transportCtrl.encrypt(requestKey, stringToArrayBuffer(token))
                  )
                )
              }

              // Sign Request
              if (extendedOptions.$sign) {
                const bodyData =
                  typeof extendedOptions.body === "string"
                    ? stringToArrayBuffer(extendedOptions.body)
                    : (extendedOptions.body ?? new ArrayBuffer(0))
                const signature = await transportCtrl.sign(transportCtrl.data.browserId, bodyData)
                extendedOptions.headers.set("x-sign", arrayBufferToBase64(signature))
              }

              // Encrypt Body if Not FormData
              if (extendedOptions.body && !(extendedOptions.body instanceof FormData)) {
                extendedOptions.headers.set("content-type", "application/json")
                console.log("Do request with body", extendedOptions.body)
                extendedOptions.body = await transportCtrl.encrypt(
                  requestKey,
                  typeof extendedOptions.body === "string"
                    ? stringToArrayBuffer(extendedOptions.body)
                    : extendedOptions.body
                )
              }
            } finally {
              try {
                if (typeof extendedOptions.$beforeSend === "function") {
                  extendedOptions.$beforeSend(extendedOptions, {url})
                }
              } catch (err) {
                console.warn("Error in beforeSend hook:", err)
              }
            }
          }

          // Abort previous request if exists
          if (abortControllers.has(url)) {
            abortControllers.get(url)?.abort()
          }
          const controller = new AbortController()
          extendedOptions.signal = controller.signal
          abortControllers.set(url, controller)
        },
        async afterFetch({context, data, response}: AfterFetchContext) {
          // Explicitly cast context.options to ExtendedFetchOptions
          const extendedOptions = context.options as ExtendedFetchOptions
          let decryptedData: any = data

          if (extendedOptions.encrypted) {
            const decrypted = arrayBufferToString(
              await transportCtrl.decrypt(extendedOptions.requestKey!, await response.arrayBuffer())
            )
            decryptedData = JSON.parse(decrypted) // Ensure proper JSON parsing if applicable
          } else {
            decryptedData = JSON.parse(data)
          }
          console.log("decryptedData", decryptedData)
          return {data: decryptedData, response}
        },
        updateDataOnError: false,
        async onFetchError({context, data, response, error}: OnFetchErrorContext) {
          if (response?.status === 401) {
            const authStore = useAuthStore()
            authStore.resetUserAccess()
            router.push(RoutesEnum.UserLogin)
          }

          if (error?.name === "AbortError") {
            console.warn("The request was aborted!")
            throw error // to log it to Sentry
          }

          // encrypt error
          const extendedOptions = context.options as ExtendedFetchOptions
          let decryptedError: ApiError = data || error

          try {
            if (response && extendedOptions.encrypted) {
              const decrypted = arrayBufferToString(
                await transportCtrl.decrypt(
                  extendedOptions.requestKey!,
                  await response.arrayBuffer()
                )
              )
              decryptedError = JSON.parse(decrypted)
            }
            decryptedError.statusCode = response?.status ?? 0
            console.log("decryptedError", decryptedError)
          } catch (error: unknown) {
            const errorStore = useErrorStore()
            errorStore.setError({error: error instanceof Error ? error : "Невідома помилка"})
            throw error // to log it to Sentry
          }

          return {error: decryptedError}
        },
      },
    } as CreateFetchOptions)
  }
  return useFetchFn(url, options) as UseFetchReturn<TRes>
}
